import React, { useMemo } from 'react';
import { ErrorMessage } from 'formik';
import BasicInput from '../../../../../../components/BasicInput';
import BasicLabel from '../../../../../../components/BasicLabel';
import BasicSelect from '../../../../../../components/BasicSelect';
import CheckboxMultiSelect from '../../../../../../components/CheckboxMultiSelect';
import CreatableValueSelect from '../../../../../../components/CreatableValueSelect';
import DateSelect from '../../../../../../components/DateSelect';
import LazyLoadSelect from '../../../../../../components/LazyLoadSelect';
import CreatableLazySelect from '../../../../../../components/LazyLoadSelect/lazy-load-creatable';
import {STUDENT_ROLE_ID} from '../../../../../../constants/roles';

const FormStep2 = ({
  values,
  options,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  getAsyncOptions: {
    getUndergraduateSchoolOptions,
    getFirmOptions,
    getOfficeLocationOptions,
  },
  params: { isRecruiter },
}) => {
  const [isStudent, hideIndustry, groupTitle, groupOptions] = useMemo(
    () => [
      values.role && Number(values.role.value) === STUDENT_ROLE_ID,
      values.role && values.role.attrs.HideIndustry,
      values.role && values.role.attrs.OwnGroups
        ? values.role.attrs.OwnGroupsTitle
        : 'Group',
      values.role && values.role.attrs.OwnGroups
        ? options.group.filter(
            group => Number(group.attrs.RoleID) === Number(values.role.value)
          )
        : options.group.filter(group => !group.attrs.RoleID),
    ],
    [values.role]
  );

  const handleChangeRole = (field, value) => {
    if (Number(value.value) === STUDENT_ROLE_ID) {
      if (!isRecruiter) {
        setFieldValue('currentFirm', null);
      }
      setFieldValue('title', null);
    } else {
      setFieldValue('degree', null);
      setFieldValue('currentSchool', null);
    }

    setFieldValue('group', null);
    setFieldValue('industry', null);

    setFieldValue(field, value);
  };

  return (
    <div className="form__step">
      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="role"
          required={true}
          text="Current Role"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="role"
          value={values.role}
          isValid={(!errors.role && !!touched.role) || !touched.role}
          labelStyles={{
            fontWeight: 'bold',
          }}
          required={true}
          placeholder="Start Typing"
          options={options.role}
          onChange={handleChangeRole}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="role"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      {isStudent ? (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <BasicLabel
            name="degree"
            required={true}
            text="Degree"
            styles={{
              fontWeight: 'bold',
            }}
          />
          <BasicSelect
            name="degree"
            value={values.degree}
            isValid={(!errors.degree && !!touched.degree) || !touched.degree}
            labelStyles={{
              fontWeight: 'bold',
            }}
            required={true}
            placeholder="Start Typing"
            options={options.degree}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />

          <ErrorMessage
            name="degree"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      ) : (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <BasicLabel
            name="title"
            required={true}
            text="Title"
            styles={{
              fontWeight: 'bold',
            }}
          />
          <BasicSelect
            name="title"
            value={values.title}
            isValid={(!errors.title && !!touched.title) || !touched.title}
            labelStyles={{
              fontWeight: 'bold',
            }}
            required={true}
            placeholder="Start Typing"
            options={options.title}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />

          <ErrorMessage
            name="title"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      )}

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <DateSelect
          required
          label={`I started ${isStudent ? `school` : `working`}`}
          name="startDate"
          controlFunc={setFieldValue}
          touchFunc={setFieldTouched}
          values={values.startDate}
          yearOptions={options.workStart.year}
          preDefinedValues={values.preDefinedStartDate}
          isValid={
            !(errors.startDate && !touched.startDate) || !touched.startDate
          }
        />

        <ErrorMessage
          name="startDate"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <LazyLoadSelect
          name="school"
          value={values.school}
          isValid={(!errors.school && !!touched.school) || !touched.school}
          label="Undergraduate School"
          labelStyles={{
            fontWeight: 'bold',
          }}
          required={true}
          placeholder="Start Typing"
          getOption={getUndergraduateSchoolOptions}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="school"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="graduationYearLabel"
          required={true}
          text="Graduation Year"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="graduationYear"
          value={values.graduationYear}
          isValid={
            (!errors.graduationYear && !!touched.graduationYear) ||
            !touched.graduationYear
          }
          placeholder="Select From List"
          options={options.graduationYear}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="graduationYear"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicInput
          label="Undergraduate Major"
          required={true}
          name="major"
          inputType="text"
          content={values.major}
          placeholder="Type here..."
          controlFunc={handleChange}
          isValid={(!errors.major && !!touched.major) || !touched.major}
          touchFunc={handleBlur}
          labelStyles={{ fontWeight: 'bold' }}
        />

        <ErrorMessage
          name="major"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="gpa"
          required={true}
          text="Undergraduate GPA"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="gpa"
          value={values.gpa}
          isValid={(!errors.gpa && !!touched.gpa) || !touched.gpa}
          placeholder="Select From List"
          options={options.gpa}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="gpa"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="mathSat"
          required={true}
          text="Math SAT"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="mathSat"
          value={values.mathSat}
          isValid={(!errors.mathSat && !!touched.mathSat) || !touched.mathSat}
          placeholder="Select From List"
          options={options.mathSat}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="mathSat"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="verbalSat"
          required={true}
          text="Verbal SAT"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="verbalSat"
          value={values.verbalSat}
          isValid={
            (!errors.verbalSat && !!touched.verbalSat) || !touched.verbalSat
          }
          placeholder="Select From List"
          options={options.verbalSat}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="verbalSat"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="writingSat"
          required={true}
          text="Writing SAT"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="writingSat"
          value={values.writingSat}
          isValid={
            (!errors.writingSat && !!touched.writingSat) || !touched.writingSat
          }
          placeholder="Select From List"
          options={options.writingSat}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="writingSat"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="actScore"
          required={true}
          text="ACT Score"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="actScore"
          value={values.actScore}
          isValid={
            (!errors.actScore && !!touched.actScore) || !touched.actScore
          }
          placeholder="Select From List"
          options={options.actScore}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="actScore"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <BasicLabel
          name="gmat"
          required={true}
          text="GMAT"
          styles={{
            fontWeight: 'bold',
          }}
        />
        <BasicSelect
          name="gmat"
          value={values.gmat}
          isValid={(!errors.gmat && !!touched.gmat) || !touched.gmat}
          placeholder="Select From List"
          options={options.gmat}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="gmat"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>

      {isStudent ? (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <CreatableValueSelect
            name="currentSchool"
            label="Current School"
            required={true}
            value={values.currentSchool}
            isValid={
              (!errors.currentSchool && !!touched.currentSchool) ||
              !touched.currentSchool
            }
            placeholder="Select From List"
            options={options.currentSchool}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />

          <ErrorMessage
            name="currentSchool"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      ) : (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <CreatableLazySelect
            isDisabled={isRecruiter}
            name="currentFirm"
            value={values.currentFirm}
            isValid={
              (!errors.currentFirm && !!touched.currentFirm) ||
              !touched.currentFirm
            }
            label="Current Firm"
            labelStyles={{
              fontWeight: 'bold',
            }}
            required={true}
            placeholder="Start Typing"
            getOption={getFirmOptions}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />

          <ErrorMessage
            name="currentFirm"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      )}

      {!isStudent && (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <CheckboxMultiSelect
            name="group"
            label={`${groupTitle} (select most applicable)`}
            labelStyles={{
              fontWeight: 'bold',
            }}
            isValid={(!errors.group && !!touched.group) || !touched.group}
            required={true}
            selectedItems={values.group}
            items={groupOptions}
            placeholder=""
            groups={false}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />
          <ErrorMessage
            name="group"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      )}

      {!hideIndustry && !isStudent && (
        <div className="form__group" style={{ marginBottom: '15px' }}>
          <CheckboxMultiSelect
            name="industry"
            label="Industry (check all that apply)"
            labelStyles={{
              fontWeight: 'bold',
            }}
            isValid={
              (!errors.industry && !!touched.industry) || !touched.industry
            }
            required={true}
            selectedItems={values.industry}
            items={options.industry}
            placeholder=""
            groups={false}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />
          <ErrorMessage
            name="industry"
            render={message => <div className="form__error">{message}</div>}
          />
        </div>
      )}

      <div className="form__group" style={{ marginBottom: '15px' }}>
        <LazyLoadSelect
          name="officeLoc"
          label={`${isStudent ? 'School' : 'Office'} Location`}
          value={values.officeLoc}
          isValid={
            (!errors.officeLoc && !!touched.officeLoc) || !touched.officeLoc
          }
          labelStyles={{
            fontWeight: 'bold',
          }}
          required={true}
          placeholder="Start Typing"
          getOption={getOfficeLocationOptions}
          onChange={setFieldValue}
          onBlur={setFieldTouched}
        />

        <ErrorMessage
          name="officeLoc"
          render={message => <div className="form__error">{message}</div>}
        />
      </div>
    </div>
  );
};

export default FormStep2;
